import { setCursor } from '../../lib/cursor';
import {
  defaultGenericElement,
  type GenericElement,
  updateElement,
} from '../../lib/element';
import {
  prepareElementForExport,
  roundExportVal,
} from '../../lib/exportImport';
import { type Plugin } from '../../lib/plugin';
import { useEditorRuntimeStore } from '../../stores/useEditorRuntimeStore';
import { useEditorStore } from '../../stores/useEditorStore';
import { fillCircle, fillRoundRect } from '../../utils/canvas';
import { hexToRgbString } from '../../utils/color';
import { type Point } from '../../utils/math';
import { general } from '../general';

import { events } from './events';
import { transformation } from './transformation';
import { getBoundsWithBezier } from './utils/bounds';
import { getPathFromPoints } from './utils/path';
import { getPolyShape } from './utils/shape';
import { getPolyTransformHandles } from './utils/transform';

export type PointBezierHandle = {
  before: Point;
  after: Point;
};

export interface GenericPolyElement extends GenericElement {
  points: Point[];
  path: Path2D;
  bezierHandles?: PointBezierHandle[];
  strokeColor: string;
  strokeWidth: number;
  isFilled?: boolean;
  isOpen?: boolean;
}

export interface PolyElement extends GenericPolyElement {
  type: 'poly';
}

export const isGenericPolyElement = (
  object: GenericElement,
): object is GenericPolyElement => {
  return (object as GenericPolyElement).points !== undefined;
};

const state = {
  newStrokeColor: '#0085FF',
  newFillColor: 'rgba(60, 135, 247, 0.4)',
  newStrokeWidth: 3,
  isDragging: false,
  draggingStartPoint: null as Point | null,
};

const actions = {
  activate: (color: string) => {
    general.actions.activate(plugin.name);
    state.newFillColor = hexToRgbString(color, 0.4);
    if (plugin.cursor != null) {
      setCursor(plugin.cursor);
    }
  },
  setStrokeColor: (color: string) => {
    state.newStrokeColor = color;
  },
  setStrokeWidth: (width: number) => {
    state.newStrokeWidth = width;
  },
  setDraggingStartPoint: (point: Point | null) => {
    state.draggingStartPoint = point;
  },
  setIsDragging: (isDragging: boolean) => {
    state.isDragging = isDragging;
  },
};

export type PolyPlugin = Plugin<
  GenericPolyElement,
  typeof actions,
  typeof state
>;

const plugin: PolyPlugin = {
  name: 'poly',
  cursor: 'crosshair',
  actions: actions,
  state: state,
  events,
  render: {
    padding: (element) => element.strokeWidth,
    draw: ({
      config,
      context,
      element,
      runtimeState,
      elementState,
      skipElementUpdate,
    }) => {
      if (!runtimeState.newElement && !element.points.length) return;
      if (element.renderedById != null) return;

      context.save();

      context.lineJoin = 'round';
      context.lineCap = 'round';
      context.lineWidth = element.strokeWidth / runtimeState.zoom.value;
      context.strokeStyle = state.newStrokeColor;
      context.fillStyle = state.newFillColor;

      if (element.isDeletedFlag) {
        context.globalAlpha = config.canvas.deletingElementOpacity;
      }

      const path = getPathFromPoints(
        element.points,
        element.bezierHandles ?? [],
        !element.isOpen,
      );

      if (!skipElementUpdate) {
        updateElement<GenericPolyElement>(elementState.elements, element, {
          path,
        });
      }
      context.stroke(path);
      context.fill(path, 'evenodd');

      context.restore();
    },
  },
  collision: {
    onlyCountDirectElementHitAfterSelection: true,
    bounds: (element) =>
      getBoundsWithBezier(
        element.points,
        element.bezierHandles,
        element.x,
        element.y,
      ),
    calculateShape: (element) => getPolyShape(element),
    isPointInShape: (point, element) => {
      const { staticCanvas, zoom } = useEditorRuntimeStore.getState();

      if (!staticCanvas) return false;

      const x = point[0] * zoom.value;
      const y = point[1] * zoom.value;

      const context = staticCanvas.getContext('2d');

      if (!context) return false;

      context.lineWidth = element.strokeWidth / zoom.value;

      const isPointInPath = context.isPointInPath(
        new Path2D(element.shape.data),
        x,
        y,
        'evenodd',
      );

      return isPointInPath;
    },
    isPointOnShape: (point, element) => {
      const { staticCanvas, zoom } = useEditorRuntimeStore.getState();
      const context = staticCanvas?.getContext('2d');

      if (!context) return false;

      context.lineWidth = element.strokeWidth / zoom.value;

      const isPointInPath = context.isPointInStroke(
        new Path2D(element.shape.data),
        point[0] * zoom.value,
        point[1] * zoom.value,
      );

      return isPointInPath;
    },
  },
  transformation,
  selection: {
    hideDefaultTransformHandles: ({ elementState }) =>
      elementState.polygonPointsVisible,
    hideDefaultSelectionBorder: ({ elementState }) =>
      elementState.polygonPointsVisible,
    renderTransformHandlesForNewElement: true,
    getTransformHandles: getPolyTransformHandles,
    renderTransformHandles: (context, handles, { state, config }) => {
      context.save();

      context.lineWidth = 1.5 / state.zoom.value;
      context.strokeStyle = config.selection.strokeStyle;
      context.fillStyle = config.selection.transformFillStyle;

      Object.keys(handles).forEach((key) => {
        const handle = handles[key];
        const [x, y, width, height] = handle;

        if (!key.includes('handleBefore') && !key.includes('handleAfter')) {
          const beforeHandle = handles[`${key}-handleBefore`];
          const afterHandle = handles[`${key}-handleAfter`];
          if (beforeHandle && afterHandle) {
            context.beginPath();
            context.moveTo(
              beforeHandle[0] + beforeHandle[2] / 2,
              beforeHandle[1] + beforeHandle[3] / 2,
            );
            context.lineTo(x + width / 2, y + height / 2);
            context.lineTo(
              afterHandle[0] + afterHandle[2] / 2,
              afterHandle[1] + afterHandle[3] / 2,
            );
            context.stroke();
            context.closePath();
          }
        }
        context.fillStyle = useEditorStore
          .getState()
          .selectedSubElementIds.includes(key)
          ? config.selection.strokeStyle
          : config.selection.transformFillStyle;

        const isBezierHandle =
          key.includes('handleBefore') || key.includes('handleAfter');

        if (isBezierHandle) {
          fillRoundRect(context, x, y, width, height, 2 / state.zoom.value);
        } else {
          fillCircle(context, x + width / 2, y + height / 2, width / 2);
        }
      });

      context.restore();
    },
    renderHoverBorder: (ctx, element, { config, state }) => {
      ctx.save();
      ctx.translate(state.scrollX, state.scrollY);

      ctx.lineWidth = config.hover.lineWidth / state.zoom.value;
      ctx.strokeStyle = config.hover.strokeStyle;

      ctx.stroke(new Path2D(element.shape.data));

      ctx.restore();
    },
    hasVisiblePointHandles: true,
  },
  export: (element) => {
    const prepared = prepareElementForExport(element) as GenericPolyElement;

    prepared.points = prepared.points.map(
      (point) => [roundExportVal(point[0]), roundExportVal(point[1])] as Point,
    );

    return prepared;
  },
  import: (element) => {
    const imported = {
      ...defaultGenericElement(),
      ...element,
    } as GenericPolyElement;

    imported.shape = getPolyShape(imported);

    return imported;
  },
};

export const poly = plugin;
